import { useIsSpecialtyBackendTranslationEnabled } from '@/app-shell/flags/workspace-flags';
import { useTranslationHelpers } from '@/hooks/useTranslationHelpers';
import { logger } from '@/utils/logger/logger';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { GET_SPECIALTIES, type ClinicianTypeInput } from '@heidi/workspace/contracts';
import {
  useQuerySpecialtiesCore,
  type RawSpecialtiesResponse,
} from '@heidi/workspace/data';
import { useCallback, useRef } from 'react';

/**
 * A specialty as the app consumes it: the wire item plus a guaranteed-renderable
 * display name.
 *
 * - `name` is the canonical English identifier — writes, URL params, logic
 *   keys, analytics. Never render it.
 * - `localizedName` is the display name in the active language — the ONLY
 *   field render sites use.
 */
export type Specialty = ClinicianTypeInput & { localizedName: string };

export type QuerySpecialtiesResponse = {
  /**
   * The single list: canonical `name` + display `localizedName`, sorted by
   * `localizedName` with `localeCompare(activeLanguage)`.
   */
  specialties: Specialty[];
  total: number;
};

export function useQuerySpecialties<T = QuerySpecialtiesResponse>(
  select?: (data: QuerySpecialtiesResponse) => T,
  options?: { country?: string; enabled?: boolean }
) {
  const { getTranslatedSpecialty, activeLanguage } = useTranslationHelpers();
  const isBackendTranslationEnabled = useIsSpecialtyBackendTranslationEnabled();

  const localize = useCallback(
    (item: ClinicianTypeInput): string => {
      if (isBackendTranslationEnabled) return item.localized_name?.trim() || item.name;
      const translated = getTranslatedSpecialty(item.name);
      return translated.startsWith('specialties.') ? item.name : translated;
    },
    [isBackendTranslationEnabled, getTranslatedSpecialty]
  );

  const toSpecialties = useCallback(
    (items: ClinicianTypeInput[]): Specialty[] =>
      items
        .map((item) => ({ ...item, localizedName: localize(item) }))
        .sort((a, b) => a.localizedName.localeCompare(b.localizedName, activeLanguage)),
    [localize, activeLanguage]
  );

  const viewRef = useRef<{
    raw: RawSpecialtiesResponse;
    fn: typeof toSpecialties;
    out: QuerySpecialtiesResponse;
  } | null>(null);

  const toView = useCallback(
    (raw: RawSpecialtiesResponse): QuerySpecialtiesResponse => {
      const cached = viewRef.current;
      if (cached && cached.raw === raw && cached.fn === toSpecialties) {
        return cached.out;
      }
      const out: QuerySpecialtiesResponse = {
        specialties: toSpecialties(raw.specialties),
        total: raw.total,
      };
      viewRef.current = { raw, fn: toSpecialties, out };
      return out;
    },
    [toSpecialties]
  );

  const selectWithView = useCallback(
    (raw: RawSpecialtiesResponse): T => {
      const data = toView(raw);
      return select ? select(data) : (data as T);
    },
    [select, toView]
  );

  return useQuerySpecialtiesCore<T>({
    country: options?.country,
    enabled: options?.enabled,
    locale: activeLanguage,
    select: selectWithView,
  });
}

/**
 * Refetch every cached specialties list (all locale/country keys, by prefix).
 */
export const invalidateSpecialties = async () => {
  try {
    await reactQueryClient.invalidateQueries({ queryKey: [GET_SPECIALTIES] });
  } catch {
    logger.error('[specialties] failed to invalidate the specialties cache');
  }
};
